
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useConversation } from "@/hooks/use-conversation";
import { Message, mapDBMessageToUIMessage } from "@/types/message";
import type { ClarificationType } from "@/components/refund/ClarificationInput";

export const useConversationState = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const threadRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isClarificationSubmitting, setIsClarificationSubmitting] = useState(false);

  const conversationData = useConversation(id);

  // Handle error state
  useEffect(() => {
    if (id && !conversationData.loading && !conversationData.conversation) {
      setError("The requested conversation could not be found. Please select a valid conversation or create a new refund request.");
    }
  }, [id, conversationData.conversation, conversationData.loading, navigate]);

  // Handle scroll to latest message
  useEffect(() => {
    if (!conversationData.isGenerating && !conversationData.isRegenerating && !conversationData.isProcessingAI && conversationData.messages.length > 0 && threadRef.current) {
      const wasGenerating = showSuccessAlert;
      
      if (wasGenerating) {
        setTimeout(() => {
          const latestMessageElement = document.getElementById(`message-${conversationData.messages[conversationData.messages.length - 1].id}`);
          if (latestMessageElement) {
            latestMessageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          } else {
            threadRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
          
          toast({
            title: "Response Generated",
            description: "We've created a response for you. Review it and send it to the merchant.",
            variant: "default",
          });
          
          setShowSuccessAlert(false);
        }, 500);
      }
    }
  }, [conversationData.isGenerating, conversationData.isRegenerating, conversationData.isProcessingAI, conversationData.messages, toast, showSuccessAlert]);

  // Set success alert when generating
  useEffect(() => {
    if (conversationData.isGenerating || conversationData.isProcessingAI) {
      setShowSuccessAlert(true);
    }
  }, [conversationData.isGenerating, conversationData.isProcessingAI]);

  // Scroll to thread when loaded
  useEffect(() => {
    if (!conversationData.loading && threadRef.current && conversationData.messages.length > 0) {
      setTimeout(() => {
        if (window.scrollY < 100) {
          threadRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [conversationData.loading, conversationData.messages.length]);

  return {
    id,
    threadRef,
    error,
    showSuccessAlert,
    isClarificationSubmitting,
    setIsClarificationSubmitting,
    conversationData,
    toast
  };
};
